package dev.lingvo.site.components.billing

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.state.WorkspaceActionClaim
import dev.lingvo.site.state.WorkspaceRequestContext
import dev.lingvo.site.state.runWorkspaceAction
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

/**
 * Sends somebody to the payment processor's own billing screens.
 *
 * **The link is minted when the button is pressed, never when the page loads.** It authenticates
 * whoever holds it, so a URL sitting in a rendered page ends up in a screen recording, a support
 * ticket, or a browser history somebody else reads. Fetching on click also means a page left open
 * overnight cannot hand out a link that has since expired.
 *
 * This is the whole answer to "my card was declined". Until it existed the product told people to
 * update their card from a screen that was nowhere in it, so an expired card meant silent churn.
 */
@Composable
internal fun PortalButton(
    ownerClaim: WorkspaceActionClaim?,
    label: String,
    variant: LButtonVariant = LButtonVariant.PRIMARY,
    invoices: Boolean = false,
    currentContext: () -> WorkspaceRequestContext? = WorkspaceRequestContext::current,
    portal: suspend (workspaceSlug: String) -> String = { workspaceSlug ->
        ApiClient.billingPortal(workspaceSlug, invoices = invoices).url
    },
    navigate: (String) -> Unit = { url -> window.location.href = url },
    launchAction: ((suspend () -> Unit) -> Unit)? = null,
) {
    val scope = rememberCoroutineScope()
    val launch: (suspend () -> Unit) -> Unit = launchAction ?: { action ->
        scope.launch { action() }
    }
    var busy by remember(ownerClaim?.key) { mutableStateOf(false) }
    // Held here rather than handed to the caller. Every page that needs this button is a page where
    // somebody's card has just failed, and making each one build its own error surface is how one
    // of them ends up swallowing the failure.
    var error by remember(ownerClaim?.key) { mutableStateOf<String?>(null) }

    LButton(
        label,
        {
            val actionClaim = ownerClaim
                ?.takeIf { it.remainsAuthorized(currentContext()) }
                ?: return@LButton
            launch {
                runWorkspaceAction(
                    claim = actionClaim,
                    currentContext = currentContext,
                    setBusy = {
                        busy = it
                        if (it) error = null
                    },
                    transport = portal,
                    onSuccess = { url ->
                        // Navigates rather than opening a tab: a popup blocker eats `window.open`
                        // on a click that went through an await, and it fails silently — somebody
                        // presses the button and nothing at all happens.
                        navigate(url)
                    },
                    onFailure = { failure ->
                        error = failure.message?.takeIf(String::isNotBlank)
                            ?: "Could not open the billing portal. Try again in a moment."
                    },
                )
            }
        },
        variant = variant,
        enabled = ownerClaim != null && !busy,
        loading = busy,
    )
    error?.let { message ->
        P(
            Modifier
                .color(ColorMode.current.toSitePalette().danger)
                .fontSize(TypeScale.Small)
                .margin(top = Space.SM, bottom = 0.px)
                .toAttrs(),
        ) {
            Text(message)
        }
    }
}
